import * as React from 'react';

import { ReactFCC } from '@notino/react-styleguide';

import { Modal, IModalProps } from './components/Modal';

type IContextModalProps = Omit<IModalProps, 'onClose' | 'onBack'> &
  Partial<Pick<IModalProps, 'onClose' | 'onBack'>>;

const DEFAULT_PROPS: IContextModalProps = {
  showBack: false,
  currentStep: 1,
  totalSteps: 1,
  modalProps: {},
};

interface ModalElementWithProps {
  element: React.ReactNode;
  props: IContextModalProps;
}
interface MultistepModalState {
  current: ModalElementWithProps;
  previous: ModalElementWithProps[];
}
interface IMultistepModalContext {
  openModal: (
    element: React.ReactNode,
    props: Partial<IContextModalProps>
  ) => void;
  animateProps: (props: Partial<IContextModalProps>) => void;
  restore: (
    current: ModalElementWithProps,
    previous: ModalElementWithProps[]
  ) => void;
  closeModal: () => void;
  transition: (
    element: React.ReactNode,
    props: Partial<IContextModalProps>
  ) => void;
  back: () => void;
  getState: () => MultistepModalState;
}

const MultistepModalContext = React.createContext<IMultistepModalContext>(
  {} as IMultistepModalContext
);

export const MultistepModalProvider: ReactFCC = ({ children }) => {
  const [step, setStep] = React.useState<MultistepModalState>({
    current: {
      element: null,
      props: DEFAULT_PROPS,
    },
    previous: [],
  });
  const [animationState, setAnimationState] =
    React.useState<Partial<IContextModalProps>>(null);

  const stateRef = React.useRef<MultistepModalState>(step);

  React.useEffect(() => {
    stateRef.current = step;
  });

  const value: IMultistepModalContext = React.useMemo(
    () => ({
      openModal: (modal, props) =>
        setStep((prev) => ({
          ...prev,
          current: {
            element: modal,
            props: { ...prev.current.props, ...props },
          },
        })),
      animateProps: setAnimationState,
      closeModal: () => {
        setStep({
          current: {
            element: null,
            props: DEFAULT_PROPS,
          },
          previous: [],
        });
      },
      transition: (newCurrent, props) => {
        setStep((prev) => ({
          current: {
            element: newCurrent,
            props: { ...prev.current.props, ...props },
          },
          previous: [...prev.previous, prev.current],
        }));
        setAnimationState(null);
      },
      back: () => {
        setStep((prev) => ({
          current: prev.previous[prev.previous.length - 1],
          previous: [...prev.previous.slice(0, -1)],
        }));
      },
      restore: (current, previous) => {
        setStep((prev) => ({
          current: {
            element: current.element,
            props: { ...prev.current.props, ...current.props },
          },
          previous: previous,
        }));
      },
      getState: () => stateRef.current,
    }),
    []
  );

  return (
    <MultistepModalContext.Provider value={value}>
      {step.current.element && (
        <Modal
          onClose={value.closeModal}
          onBack={value.back}
          {...step.current.props}
          showBack={step.previous.length > 0}
          {...animationState}
        >
          {step.current.element}
        </Modal>
      )}

      {children}
    </MultistepModalContext.Provider>
  );
};

export const useMultistepModal = () => {
  return React.useContext(MultistepModalContext);
};
